<template>
  <label class="switch__container">
    <div v-if="label != null && !labelRight" class="label switch__label">
      {{ label }}
    </div>
    <div class="switch__alignment">
      <div class="switch" :class="{ 'is-on': value, 'is-disabled': disabled }">
        <input
          :checked="value"
          type="checkbox"
          class="switch__input"
          :disabled="disabled"
          @change="onChange"
        />

        <div
          ref="handle"
          class="switch__handle"
          :class="{
            switch__handle_checked: value,
            switch__handle_unchecked: !value
          }"
        ></div>
      </div>
      <span class="switch__statustext">
        <slot v-if="$slots['default']"></slot>
        <template v-else-if="!labelRight">
          <template v-if="value">{{ textOnTranslated }}</template>
          <template v-if="!value">{{ textOffTranslated }}</template>
        </template>
        <template v-else>{{ label }}</template>
      </span>
    </div>
  </label>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
  value?: boolean;
  label?: string | null;
  textOn?: string | null;
  textOff?: string | null;
  labelRight?: boolean;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  value: false,
  label: null,
  textOn: null,
  textOff: null,
  labelRight: false,
  disabled: false
});
const emit = defineEmits<{
  (e: "update:value", value: boolean): void;
}>();

const { t } = useI18n();

const textOnTranslated = computed(() => {
  if (props.textOn) return props.textOn;
  return t("labels.on");
});
const textOffTranslated = computed(() => {
  if (props.textOff) return props.textOff;
  return t("labels.off");
});

function onChange(event: Event) {
  const target = event.target as HTMLInputElement;
  emit("update:value", target.checked);
}
</script>

<style lang="scss">
.switch {
  $handle-width: 14px;
  $switch-padding: 3px;
  $switch-border-width: 2px;
  touch-action: manipulation;

  position: relative;
  display: inline-block;
  background-color: var(--color-background-close);
  padding: $switch-padding;
  height: $handle-width + ($switch-padding * 2) + ($switch-border-width * 2);
  border-radius: 2em;
  border: $switch-border-width solid var(--color-text);
  width: (3.2 * $handle-width) + ($switch-padding * 2);
  overflow: hidden;
  flex-shrink: 0;
  transition:
    background-color 125ms,
    border-color 125ms;

  &:hover {
    background-color: var(--color-background-close-hover);
  }

  &.is-on {
    border-color: var(--color-action);
    background-color: var(--color-action);
  }
  &.is-disabled {
    border-color: var(--color-text-lighter);
    background-color: var(--color-text-lighter);
  }

  &__container {
    display: block;
    & + & {
      margin-top: 0.5em;
    }
  }

  &__alignment {
    display: flex;
    align-items: center;
  }

  &__input {
    position: absolute;
    opacity: 0;
    left: 0;
    top: 0;
    pointer-events: none;
  }

  &__handle {
    height: $handle-width;
    width: $handle-width;
    border-radius: 50%;
    background-color: var(--color-text);
    transform: translateX(0);
    transition:
      transform 150ms ease-out,
      background-color 200ms;

    &_checked {
      transform: translateX(27px);
      background-color: var(--color-background-close);
    }
    &_unchecked {
      transform: translateX(0);
    }

    .is-disabled & {
      background-color: var(--color-text-light);
    }
  }

  &__label {
    margin-top: 0;
    margin-bottom: 0.25rem;
  }
  &__container:focus-within &__label {
    color: var(--color-text);
  }

  &__statustext {
    font-size: var(--font-size-label);
    margin-left: 0.75rem;
    display: block;
    width: 100%;
  }

  &:focus-within {
    box-shadow: 0 0 0 2px var(--color-action);
  }
}
</style>
